import { useEffect, useRef, type ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import type { Role } from '@electrodavisan/shared';
import { ROUTES } from '../app/config';
import { ErrorState } from '../components/ui';
import { FullScreenLoader } from '../components/layout/FullScreenLoader';
import { isExpiredSessionError, realAuthError } from './errors';
import { anotarReautenticacion, reautenticacionReciente, rememberReturnTo } from './oidc';
import { hasRole, useCurrentUser } from './useSession';

/**
 * Exige sesion. Si no la hay se redirige a Keycloak recordando a donde
 * queria ir el usuario.
 *
 * El guard NO lanza el login desde un efecto en bucle: mientras
 * `auth.isLoading` o `activeNavigator` esten activos simplemente espera.
 *
 * ## Sesion caducada: se resuelve, no se denuncia
 *
 * Una sesion que expira en Keycloak llegaba aqui como «Session not active» y
 * acababa en la pantalla de «No se pudo iniciar sesion. Comprueba que Keycloak
 * este disponible». Keycloak estaba bien; lo que habia caducado era la sesion.
 * El usuario pulsaba «Reintentar» y entraba, porque reintentar era justo lo
 * que hacia falta: ahora lo hace la aplicacion y el no ve nada.
 *
 * Se intenta UNA vez. Si al volver sigue sin valer, entonces si se ensena el
 * error: insistir seria un bucle del que no se puede salir.
 */
export function RequireAuth({ children }: { children: ReactNode }): JSX.Element {
  const auth = useAuth();
  const location = useLocation();
  const reautenticando = useRef(false);

  // Una sesion caducada no es una averia: se vuelve a Keycloak y se acabo.
  // Solo cuenta como error si ya lo intentamos hace nada y seguimos aqui.
  const caducada = isExpiredSessionError(auth.error) && !reautenticacionReciente();

  // `login_required` solo significa "todavia no hay sesion": no bloquea el
  // flujo, simplemente toca ir a Keycloak.
  const fatalError = caducada ? null : realAuthError(auth.error);

  useEffect(() => {
    if (!caducada || auth.activeNavigator || reautenticando.current) return;

    reautenticando.current = true;
    anotarReautenticacion();
    rememberReturnTo(location.pathname + location.search);

    /*
     * Se borra el usuario antes de volver. Lo que hay guardado incluye un
     * refresh token que Keycloak ya rechazo, y dejarlo ahi haria que la
     * siguiente renovacion automatica volviese a fallar con el mismo error
     * nada mas entrar.
     */
    void auth.removeUser().finally(() => {
      void auth.signinRedirect();
    });
  }, [auth, caducada, location.pathname, location.search]);

  useEffect(() => {
    if (auth.isLoading || auth.activeNavigator || auth.isAuthenticated) return;
    if (fatalError || caducada) return;

    rememberReturnTo(location.pathname + location.search);
    void auth.signinRedirect();
  }, [auth, caducada, fatalError, location.pathname, location.search]);

  if (fatalError) {
    return (
      <div className="flex min-h-screen items-center justify-center p-6">
        <ErrorState
          title="No se pudo iniciar sesion"
          description={
            <>
              {fatalError.message}
              <br />
              Comprueba que Keycloak este disponible y vuelve a intentarlo.
            </>
          }
          onRetry={() => {
            void auth.signinRedirect();
          }}
        />
      </div>
    );
  }

  if (!auth.isAuthenticated) {
    return (
      <FullScreenLoader
        message={caducada ? 'Renovando tu sesion...' : 'Verificando tu sesion...'}
      />
    );
  }

  return <>{children}</>;
}

/**
 * Exige uno de los roles indicados.
 *
 * Esto es SOLO experiencia de usuario: evita mostrar una pantalla que
 * fallaria igualmente. La autorizacion de verdad la hace la API en cada
 * peticion.
 */
export function RequireRole({
  roles,
  children,
}: {
  roles: Role[];
  children: ReactNode;
}): JSX.Element {
  const { data: user, isLoading, isError } = useCurrentUser();

  if (isLoading) {
    return <FullScreenLoader message="Comprobando permisos..." />;
  }

  if (isError || !user) {
    return <Navigate to={ROUTES.forbidden} replace />;
  }

  if (!hasRole(user, ...roles)) {
    return <Navigate to={ROUTES.forbidden} replace />;
  }

  return <>{children}</>;
}
